import { Close as CloseIcon, TrendingUp as NavTrendingUpIcon } from '@mui/icons-material';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { AddPropertyModal } from './AddPropertyModal';
import { AdvisorSidebar } from './AdvisorSidebar';
import { ChatPanel } from './chat';
import { PANEL_WIDTH } from './chat/ChatPanel';
import { ClientSidebar } from './ClientSidebar';
import { HOAIcon, SellerDisclosuresIcon } from './navigation/SidebarIcons';
import PropertyHeader from './PropertyHeader';
import { PropertySelector } from './PropertySelector';

import { ModeSwitch } from '@/components/dashboard/UserSwitcher';
import { ToolsSurface } from '@/components/ds';
import { AppThemeContext } from '@/context/AppThemeProvider';
import { useAuthContext } from '@/context/AuthProvider';
import { DashboardLayoutProvider, useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useChatNotifications } from '@/hooks/useChatNotifications';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useHOAChecklist } from '@/hooks/useHOAChecklist';
import { useHomeHQAccess } from '@/hooks/useHomeHQAccess';
import { useUserRole } from '@/hooks/useUserRole';
import { DocumentClassificationType } from '@/models/DocumentRecord';
import { withViewTransition } from '@/utils/viewTransition';

const SIDEBAR_WIDTH = 280;
const COLLAPSED_LEFT_WIDTH = 56;
// One gutter for the whole frame on a computer: the side panels' space above and below, the left rail to the main
// area, and the main area to the window's right edge. Only the right panel may cover the main area
const GUTTER = 20;

const ViewErrorFallback = () => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="h6" gutterBottom>
      This view encountered an error
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Try navigating to a different page. The team has been notified.
    </Typography>
  </Box>
);

/** 2-line hamburger icon matching dd.hauserhq.com */
const HamburgerIcon = () => (
  <svg
    width="20"
    height="12"
    viewBox="0 0 20 12"
    fill="none"
    stroke="#f3f3f3"
    strokeWidth="1.8"
    strokeLinecap="round">
    <line x1="0" y1="1" x2="20" y2="1" />
    <line x1="0" y1="11" x2="20" y2="11" />
  </svg>
);

/* eslint-disable max-len */
const HauserWordmark = ({ height = 28 }: { height?: number }) => (
  <svg
    height={height}
    viewBox="0 0 287.31 55.61"
    style={{ width: 'auto' }}
    xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#fff"
      d="M19.7,55.45v-24.17h-9.85v24.17H0V.16h9.85v22.04h9.85V.16h9.85v55.29h-9.85Z"
    />
    <path
      fill="#fff"
      d="M124.14,55.61h-12.93c-4.97,0-8.28-3.4-8.28-8.37V.16h9.85v45.89c0,.16.08.32.32.32h9.22c.16,0,.32-.16.32-.32V.16h9.85v47.08c0,4.98-3.39,8.37-8.35,8.37Z"
    />
    <path
      fill="#fff"
      d="M173.8,38.23l-15.92-15.8c-2.29-2.21-3.47-4.9-3.47-8.29v-5.85c0-4.9,3.31-8.29,8.28-8.29h12.93c4.97,0,8.35,3.4,8.35,8.29v8.61h-9.85v-7.35c0-.24-.16-.32-.32-.32h-9.22c-.24,0-.32.08-.32.32v5.45c0,.32.08.47.32.71l15.92,15.8c2.21,2.21,3.47,4.9,3.47,8.29v7.42c0,4.98-3.39,8.37-8.35,8.37h-12.93c-4.97,0-8.28-3.4-8.28-8.37v-8.61h9.85v7.43c0,.16.08.32.32.32h9.22c.16,0,.32-.16.32-.32v-7.11c0-.32-.16-.47-.32-.71Z"
    />
    <path
      fill="#fff"
      d="M207.85,55.45V.16h25.61v9.08h-15.76v13.11h11.82v8.69h-11.82v15.32h15.76v9.08h-25.61Z"
    />
    <path
      fill="#fff"
      d="M276.98,55.45l-5.6-21.56h-4.57v21.56h-9.85V.16h21.2c4.96,0,8.35,3.4,8.35,8.29v17.14c0,4.03-2.21,7.03-5.83,7.98l6.62,21.88h-10.32ZM266.81,24.8h9.54c.16,0,.32-.08.32-.32v-14.93c0-.24-.16-.32-.32-.32h-9.54v15.56Z"
    />
    <path
      fill="#fff"
      d="M72.46,0h-12.42l-12.34,55.61h37.09L72.46,0ZM60.49,45.53l5.47-27.42.25-1.99.25,1.99,5.53,27.42h-11.5Z"
    />
  </svg>
);

/** Nav icons matching dd.hauserhq.com — 15×15, stroke style */
const NavSummaryIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" />
  </svg>
);
const NavConditionIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const NavContextIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const NavInsightsIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);
const NavHomeHubIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const NavCalculatorIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="10" y2="10" />
    <line x1="14" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="10" y2="14" />
    <line x1="14" y1="14" x2="16" y2="14" />
    <line x1="8" y1="18" x2="10" y2="18" />
    <line x1="14" y1="18" x2="16" y2="18" />
  </svg>
);
const NavToolsIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M14.7 6.3a4 4 0 0 0-5 5L4 17v3h3l5.7-5.7a4 4 0 0 0 5-5l-2.4 2.4-3-3 2.4-2.4z" />
  </svg>
);
const NavRecordsIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const NavVendorsIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
    <circle cx="12" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const NavCalendarIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
/* eslint-enable max-len */

const mobileNavItems = [
  { path: '/dashboard/summary', label: 'Overview', icon: <NavSummaryIcon /> },
  { path: '/dashboard/condition-score', label: 'The Home', icon: <NavConditionIcon /> },
  { path: '/dashboard/context-score', label: 'The Area', icon: <NavContextIcon /> },
  { path: '/dashboard/repair-planner', label: 'Repair Planner', icon: <NavInsightsIcon /> },
  {
    path: '/dashboard/market-insights',
    label: 'Market Insights',
    // Fixed 15x15 slot matches every other icon's footprint (so the tile's
    // height/row stretch doesn't change), with overflow: visible so the
    // glyph itself can render larger and centered inside it — MUI's
    // TrendingUp icon has more internal padding baked into its viewBox than
    // the hand-drawn icons above, so matching the bounding box alone left
    // it looking visibly smaller/thinner than its siblings.
    icon: (
      <Box
        sx={{
          height: 15,
          width: 15,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}>
        <NavTrendingUpIcon sx={{ fontSize: 20 }} />
      </Box>
    ),
  },
];

/** Dropdown overlay menu matching dd.hauserhq.com */
const MobileDropdownMenu = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdvisor } = useUserRole();
  const { selectedProperty } = usePropertiesContext();
  const { openInbox } = useDashboardLayout();
  // Mirrors ClientSidebar — owner-mode is per-selected-property so a
  // mixed-state user (owned home + consideration property) sees the
  // right dropdown for each.
  const isOwnerMode = selectedProperty?.isOwned ?? false;
  // Same data checks desktop's useDueDiligenceNavItems uses — the
  // mobile menu is a hand-maintained list, not a shared component, so this
  // has to be kept in sync by hand rather than reusing that hook directly
  // (desktop's icon set doesn't match the local Nav*Icon style this menu
  // uses elsewhere).
  const propertyId = selectedProperty?.property.id;
  const { data: hoaData } = useHOAChecklist(propertyId);
  const { documentRecords } = useDocumentRecords(propertyId);
  const hasSellerDisclosures = (documentRecords ?? []).some(
    (record) =>
      record.documentType === DocumentClassificationType.SELLER_DISCLOSURE &&
      record.sellerDisclosureManifest,
  );
  const hoaItem = hoaData
    ? { path: '/dashboard/hoa', label: 'HOA', icon: <HOAIcon size={15} /> }
    : null;
  const sellerDisclosuresItem = hasSellerDisclosures
    ? {
        path: '/dashboard/seller-disclosures',
        label: 'Seller Disclosures',
        icon: <SellerDisclosuresIcon size={15} />,
      }
    : null;
  const { logOut } = useAuthContext();
  const appTheme = useContext(AppThemeContext);
  const handleNav = (path: string) => {
    withViewTransition(() => navigate(path));
    onClose();
  };
  const handleLogout = async () => {
    onClose();
    await logOut();
    navigate('/login');
  };

  // Shared tile renderer — used for every nav item in both buyer and
  // owner modes. Keeps styling consistent across the various sections.
  const renderTile = (
    path: string,
    label: string,
    icon: React.ReactNode,
    options?: { onClick?: () => void; activeMatch?: 'exact' | 'startsWith' },
  ) => {
    const match = options?.activeMatch ?? 'exact';
    const isActive =
      match === 'exact' ? location.pathname === path : location.pathname.startsWith(path);
    return (
      <Box
        key={path + label}
        onClick={options?.onClick ?? (() => handleNav(path))}
        data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '5px',
          padding: '10px 0',
          borderRadius: '12px',
          cursor: 'pointer',
          background: isActive ? 'var(--tx-fill-3)' : 'var(--tx-fill-2)',
          border: 0,
          color: isActive ? 'var(--tx-text)' : 'var(--tx-faint)',
          transition: 'all 0.15s',
          '&:active': { background: 'rgba(255, 255, 255, 0.15)' },
        }}>
        {icon}
        <Box component="span" sx={{ fontSize: '13px', fontWeight: 500, letterSpacing: '-0.02em' }}>
          {label}
        </Box>
      </Box>
    );
  };

  const sectionLabelStyle = {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--tx-muted)',
  } as const;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          background: 'var(--tx-backdrop)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />
      {/* Dropdown card */}
      <Box
        sx={{
          position: 'fixed',
          top: '80px',
          left: '10px',
          right: '10px',
          zIndex: 1203,
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '20px',
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0)' : 'translateY(-20px)',
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
        {isOwnerMode && !isAdvisor ? (
          // ─── OWNER MODE ──────────────────────────────────────────
          // Mirrors the desktop ClientSidebar owner ordering:
          //   1. Homeowner (Home HQ + owner home modules)
          //   2. Tools (Calculators)
          // Due Diligence + Transaction sections drop out post-purchase.
          // Advisors never enter owner mode — they always need their
          // Clients / Properties / Messages tools.
          <>
            <Box sx={{ ...sectionLabelStyle, padding: '10px 0 16px' }}>Homeowner</Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {renderTile('/dashboard/home-hub', 'Home HQ', <NavHomeHubIcon />)}
            </Box>
            <Box sx={{ display: 'flex', gap: '8px', mt: '8px' }}>
              {renderTile('/dashboard/home-hub/repair-plan', 'Repair Plan', <NavToolsIcon />)}
              {renderTile('/dashboard/home-hub/records', 'Records', <NavRecordsIcon />)}
            </Box>
            <Box sx={{ display: 'flex', gap: '8px', mt: '8px' }}>
              {renderTile('/dashboard/home-hub/directory', 'Directory', <NavVendorsIcon />)}
              {renderTile('/dashboard/home-hub/calendar', 'Calendar', <NavCalendarIcon />)}
            </Box>

            <Box sx={{ ...sectionLabelStyle, padding: '20px 0 16px' }}>Tools</Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {renderTile('/dashboard/calculators', 'Calculators', <NavCalculatorIcon />)}
            </Box>
          </>
        ) : (
          // ─── BUYER MODE (pre-purchase) ──────────────────────────
          <>
            {/* DUE DILIGENCE label */}
            <Box
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--tx-muted)',
                padding: '10px 0 16px',
              }}>
              Due Diligence
            </Box>

            {/* DD tiles — masonry grid. Overview alone, The Home/The Area
                paired, then property-specific document modules share a row
                when both are available (or span the row when only one is),
                followed by Repair Planner / Market Insights paired. */}
            {(() => {
              const rows: Array<Array<(typeof mobileNavItems)[number]>> = [
                [mobileNavItems[0]],
                [mobileNavItems[1], mobileNavItems[2]],
                ...(sellerDisclosuresItem && hoaItem
                  ? [[sellerDisclosuresItem, hoaItem]]
                  : sellerDisclosuresItem
                  ? [[sellerDisclosuresItem]]
                  : hoaItem
                  ? [[hoaItem]]
                  : []),
                [mobileNavItems[3], mobileNavItems[4]],
              ];
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {rows.map((row, ri) => (
                    <Box key={ri} sx={{ display: 'flex', gap: '8px' }}>
                      {row.map(({ path, label, icon }) => {
                        const isActive = location.pathname === path;
                        return (
                          <Box
                            key={path}
                            onClick={() => handleNav(path)}
                            data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                            sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '10px 0',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              background: isActive
                                ? 'rgba(255, 255, 255, 0.30)'
                                : 'rgba(255, 255, 255, 0.08)',
                              border: 0,
                              color: isActive ? 'var(--tx-text)' : 'var(--tx-faint)',
                              transition: 'all 0.15s',
                              '&:active': { background: 'rgba(255, 255, 255, 0.15)' },
                            }}>
                            {icon}
                            <Box
                              component="span"
                              sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                letterSpacing: '-0.02em',
                                whiteSpace: 'nowrap',
                              }}>
                              {label}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              );
            })()}

            {/* TOOLS label */}
            <Box
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--tx-muted)',
                padding: '20px 0 16px',
              }}>
              Tools
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Row 1: Clients, Properties, Messages */}
              {isAdvisor && (
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  {[
                    {
                      path: '/dashboard/clients',
                      label: 'Clients & Deals',
                      icon: (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      ),
                    },
                    {
                      path: '/dashboard/properties',
                      label: 'Properties',
                      icon: (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                      ),
                    },
                    {
                      path: '/dashboard/inbox',
                      label: 'Messages',
                      icon: (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      ),
                    },
                  ].map(({ path, label, icon }) => {
                    const isActive = location.pathname.startsWith(path);
                    const isMessages = label === 'Messages';
                    return (
                      <Box
                        key={path}
                        onClick={
                          isMessages
                            ? () => {
                                openInbox();
                                onClose();
                              }
                            : () => handleNav(path)
                        }
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '10px 0',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          background: isActive
                            ? 'rgba(255, 255, 255, 0.30)'
                            : 'rgba(255, 255, 255, 0.08)',
                          border: 0,
                          color: isActive ? 'var(--tx-text)' : 'var(--tx-faint)',
                          transition: 'all 0.15s',
                          '&:active': { background: 'rgba(255, 255, 255, 0.15)' },
                        }}>
                        {icon}
                        <Box
                          component="span"
                          sx={{ fontSize: '13px', fontWeight: 500, letterSpacing: '-0.02em' }}>
                          {label}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {/* Row 2: Calculators */}
              {(() => {
                const isActive = location.pathname === '/dashboard/calculators';
                return (
                  <Box
                    onClick={() => handleNav('/dashboard/calculators')}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '10px 0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: isActive
                        ? 'rgba(255, 255, 255, 0.30)'
                        : 'rgba(255, 255, 255, 0.08)',
                      border: 0,
                      color: isActive ? 'var(--tx-text)' : 'var(--tx-faint)',
                      transition: 'all 0.15s',
                      '&:active': { background: 'rgba(255, 255, 255, 0.15)' },
                    }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <rect x="4" y="2" width="16" height="20" rx="2" />
                      <line x1="8" y1="6" x2="16" y2="6" />
                      <line x1="8" y1="10" x2="10" y2="10" />
                      <line x1="14" y1="10" x2="16" y2="10" />
                      <line x1="8" y1="14" x2="10" y2="14" />
                      <line x1="14" y1="14" x2="16" y2="14" />
                      <line x1="8" y1="18" x2="10" y2="18" />
                      <line x1="14" y1="18" x2="16" y2="18" />
                    </svg>
                    <Box
                      component="span"
                      sx={{ fontSize: '13px', fontWeight: 500, letterSpacing: '-0.02em' }}>
                      Calculators
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </>
        )}

        {/* ACCOUNT label, with the Backlight switch (Day / Night) at its right, as in the computer's account menu */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--tx-muted)',
            padding: '14px 0 6px',
          }}>
          Account
          {appTheme && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box component="span" sx={{ fontSize: 12, fontWeight: 500 }}>
                Backlight
              </Box>
              <ModeSwitch mode={appTheme.mode} onChange={appTheme.setMode} />
            </Box>
          )}
        </Box>

        {/* Settings & Logout — same tile style, horizontal icon+label, shorter */}
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {[
            {
              action: () => handleNav('/dashboard/settings'),
              label: 'Settings',
              isActive: location.pathname.includes('/settings'),
              icon: (
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path
                    d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06
                    a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33
                    1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09
                    A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06
                    a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15
                    a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09
                    A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06
                    a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68
                    a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09
                    a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06
                    a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9
                    a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09
                    a1.65 1.65 0 0 0-1.51 1z"
                  />
                </svg>
              ),
            },
            {
              action: handleLogout,
              label: 'Logout',
              isActive: false,
              icon: (
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              ),
            },
          ].map(({ action, label, icon, isActive }) => (
            <Box
              key={label}
              onClick={action}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '8px 0',
                borderRadius: '12px',
                cursor: 'pointer',
                background: isActive ? 'var(--tx-fill-3)' : 'var(--tx-fill-2)',
                border: 0,
                color: isActive ? 'var(--tx-text)' : 'var(--tx-faint)',
                transition: 'all 0.15s',
                '&:active': { background: 'rgba(255, 255, 255, 0.15)' },
              }}>
              {icon}
              <Box
                component="span"
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                }}>
                {label}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

const DashboardLayoutContent = () => {
  const {
    isLeftSidebarCollapsed,
    setLeftSidebarCollapsed,
    isAskHauserOpen,
    openAskHauser,
    closeAskHauser,
    isChatOpen,
    openChat,
    closeChat,
    openTasksDrawer,
  } = useDashboardLayout();
  const { properties, selectedProperty, isInitialFetchingProperties, isFetchingProperties } =
    usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isNarrow = useMediaQuery('(max-width: 767px)');
  const isMidWidth = useMediaQuery('(max-width: 1400px)');
  const { canAccessAdmin, isAdvisor } = useUserRole();
  const { data: homeHQAccess } = useHomeHQAccess({ enabled: !canAccessAdmin && !isAdvisor });
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const independentOwnerPanelClosedForProperty = useRef<string | null>(null);
  const isIndependentOwnerView =
    !isAdvisor &&
    !canAccessAdmin &&
    !isLoadingGroups &&
    groups.length === 0 &&
    selectedProperty?.isOwned === true;

  useEffect(() => {
    const key = selectedProperty?.property.id ?? null;
    if (!isIndependentOwnerView || !key) return;
    if (independentOwnerPanelClosedForProperty.current === key) return;
    independentOwnerPanelClosedForProperty.current = key;
    closeAskHauser();
    closeChat();
  }, [closeAskHauser, closeChat, isIndependentOwnerView, selectedProperty?.property.id]);

  // Auto-collapse sidebar at mid-width breakpoint — only on breakpoint change
  const prevMidWidth = useRef(isMidWidth);
  useEffect(() => {
    if (isMidWidth !== prevMidWidth.current) {
      prevMidWidth.current = isMidWidth;
      setLeftSidebarCollapsed(isMidWidth);
    }
  }, [isMidWidth, setLeftSidebarCollapsed]);
  // 27": the right panel docks instead of overlaying, so sidebar, page and panel are all in view at once; it still
  // closes and the page takes the room back. Below this width it overlays as before.
  const DOCK_MIN_WIDTH = 1920;
  const [canDockPanel, setCanDockPanel] = useState(() => window.innerWidth >= DOCK_MIN_WIDTH);
  useEffect(() => {
    const onResize = () => setCanDockPanel(window.innerWidth >= DOCK_MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [addPropertyOpen, setAddPropertyOpen] = useState(false);
  const hasMultipleProperties = properties && properties.length > 1;
  const showMobilePropertySelector = isAdvisor || hasMultipleProperties;
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const isCalculatorsPage = location.pathname.includes('/calculators');

  const scrollUpAccumulator = useRef(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const y = el.scrollTop;
    if (y > lastScrollY.current && y > 60) {
      setMobileNavHidden(true);
      scrollUpAccumulator.current = 0;
    } else if (y < lastScrollY.current) {
      scrollUpAccumulator.current += lastScrollY.current - y;
      if (scrollUpAccumulator.current > (isCalculatorsPage ? 80 : 150)) {
        setMobileNavHidden(false);
      }
    }
    lastScrollY.current = y;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!isMobile || !el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isMobile, handleScroll]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  const isSettingsPage = location.pathname.includes('/settings');
  const isSellerDisclosuresPage = location.pathname.includes('/seller-disclosures');
  const isInboxPage = location.pathname.includes('/inbox');
  const isPropertiesPage = location.pathname.includes('/properties');
  const isClientsPage = location.pathname.includes('/clients');
  // The paywall replaces Home HQ for users without access; tasks and chat
  // are part of what they're paying for, so hide them there too.
  const isHomeHQPage =
    location.pathname.startsWith('/dashboard/home-hub') ||
    location.pathname.startsWith('/dashboard/plans');
  const isHomeHQLocked = isHomeHQPage && !!homeHQAccess && !homeHQAccess.hasAccess;
  // Today is the advisor's daily greeting, about no one property: no tools panel (its tasks, Ask Hauser and messages
  // are per property; Today's widgets cover them across every client)
  const isTodayPage = location.pathname.startsWith('/dashboard/today');
  const hideRightSidebarAndChat = isSettingsPage || isHomeHQLocked || isTodayPage;
  const panelDocked = canDockPanel && (isAskHauserOpen || isChatOpen) && !hideRightSidebarAndChat;
  const buyerHasNoProperties =
    !isAdvisor &&
    !isInitialFetchingProperties &&
    !isFetchingProperties &&
    Array.isArray(properties) &&
    properties.length === 0;
  const hidePropertySelector =
    isCalculatorsPage || isClientsPage || isPropertiesPage || isInboxPage || isSettingsPage || isTodayPage;
  const dueDiligenceRoutes = [
    '/dashboard/summary',
    '/dashboard/condition-score',
    '/dashboard/context-score',
    '/dashboard/seller-disclosures',
    '/dashboard/inspection-report',
    '/dashboard/repair-planner',
    // Old URL — kept here too since it still briefly matches during the
    // Router.tsx redirect to /dashboard/repair-planner.
    '/dashboard/insights',
    '/dashboard/market-insights',
  ];
  const showPropertyHeader = dueDiligenceRoutes.some((route) =>
    location.pathname.startsWith(route),
  );
  useChatNotifications();

  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile App Bar — fixed glass card matching dd.hauserhq.com */}
      {isMobile && !(isInboxPage && !isNarrow) && (
        <ToolsSurface>
          <Box
            sx={{
              position: 'fixed',
              top: mobileNavHidden ? '-80px' : '12px',
              left: '10px',
              right: '10px',
              height: '60px',
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
              backdropFilter: 'blur(10px) saturate(180%)',
              WebkitBackdropFilter: 'blur(10px) saturate(180%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '16px',
              pl: '20px',
              zIndex: 1202,
              transition: 'top 0.3s ease',
            }}>
            <Box
              onClick={() => navigate('/dashboard/summary')}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <HauserWordmark height={22} />
            </Box>
            <IconButton
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              data-testid="mobile-menu-btn"
              sx={{
                p: '6px',
                '&:hover': { backgroundColor: 'transparent' },
              }}>
              {mobileDrawerOpen ? (
                <CloseIcon sx={{ fontSize: 20, color: 'var(--tx-text)' }} />
              ) : (
                <HamburgerIcon />
              )}
            </IconButton>
          </Box>
          {/* Dropdown menu overlay */}
          <MobileDropdownMenu open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} />
        </ToolsSurface>
      )}

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Left Sidebar - overlay with frosted glass */}
        {(!isMobile || (isInboxPage && !isNarrow)) && (
          <Box
            id="dashboard-left-sidebar"
            sx={{
              position: 'fixed',
              ...(isMobile
                ? { left: 0, top: 0, height: '100dvh' }
                : { left: 0, top: `${GUTTER}px`, bottom: `${GUTTER}px`, height: 'auto' }),
              width:
                isLeftSidebarCollapsed || (isMobile && isInboxPage)
                  ? COLLAPSED_LEFT_WIDTH
                  : SIDEBAR_WIDTH,
              zIndex: 1200,
              transition: 'width 0.2s ease-in-out',
              borderRadius: isMobile ? 0 : '0 25px 25px 0',
              boxShadow: isMobile ? undefined : 'var(--tx-shadow)',
            }}>
            <ToolsSurface>{isAdvisor ? <AdvisorSidebar /> : <ClientSidebar />}</ToolsSurface>
          </Box>
        )}
        {/* Backdrop when sidebar is overlay at mid-width */}
        {isMidWidth && !isLeftSidebarCollapsed && !isMobile && (
          <Box
            onClick={() => setLeftSidebarCollapsed(true)}
            onWheel={(e) => scrollRef.current?.scrollBy(0, e.deltaY)}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1199,
              background: 'var(--tx-backdrop)',
            }}
          />
        )}

        {/* Main Content - flexible */}
        <Box
          id="dashboard-main-content"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            ...(!isMobile && {
              ml: `${
                (isMidWidth
                  ? COLLAPSED_LEFT_WIDTH
                  : isLeftSidebarCollapsed
                  ? COLLAPSED_LEFT_WIDTH
                  : SIDEBAR_WIDTH) + GUTTER
              }px`,
              // the right panel and its pull tabs are the one layer allowed to cover the page, so no lane is kept for them,
              // except on a 27" where an open panel docks: the page keeps its lane clear of the panel and its edge tabs
              mr: `${panelDocked ? PANEL_WIDTH + 36 + GUTTER : GUTTER}px`,
              transition: 'margin-left 0.2s ease-in-out, margin-right 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            }),
            // no width cap: every page uses the space the frame gives it
          }}>
          {/* the property header stays put above the scroll on a computer: the property is always in view, and the
              area below it has a fixed height that a page's instrument (The Home's score card) can fill exactly */}
          {!isMobile && showPropertyHeader && !isInboxPage && !buyerHasNoProperties && <PropertyHeader />}
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: isSellerDisclosuresPage ? 'flex' : 'block',
              flexDirection: isSellerDisclosuresPage ? 'column' : undefined,
              overflowY: isInboxPage || (isSellerDisclosuresPage && !isMobile) ? 'hidden' : 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              ...(isMobile && {
                // one 10px rhythm under the menu bar (12–72): 10px to the selector (82, 64 tall) or the page, then 10px to
                // the first card; pages add no top padding of their own on a phone
                paddingTop: showMobilePropertySelector && !hidePropertySelector ? '156px' : '82px',
              }),
            }}>
            {/* Mobile on-page property selector */}
            {isMobile && showMobilePropertySelector && !hidePropertySelector && (
              <Box
                sx={{
                  position: 'fixed',
                  top: mobileNavHidden ? '-80px' : '82px',
                  left: '10px',
                  right: '10px',
                  zIndex: 1201,
                  transition: 'top 0.3s ease',
                }}>
                <PropertySelector />
              </Box>
            )}
            {buyerHasNoProperties && showPropertyHeader ? (
              <Box
                data-testid="no-properties-placeholder"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 4,
                  minHeight: '60vh',
                }}>
                <Typography variant="h6" sx={{ color: 'var(--hx-ground-ink)', fontWeight: 500, mb: 1.5 }}>
                  No properties yet
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--hx-ground-muted)', maxWidth: 360 }}>
                  Talk to your advisor to be invited to view a property you are interested in.
                </Typography>
              </Box>
            ) : (
              <Sentry.ErrorBoundary fallback={<ViewErrorFallback />} key={location.key}>
                <Outlet />
              </Sentry.ErrorBoundary>
            )}
          </Box>
        </Box>
      </Box>

      {/* Chat Panel - available on all screen sizes */}
      {!hideRightSidebarAndChat && (
        <ToolsSurface>
        <ChatPanel
          open={isAskHauserOpen || isChatOpen}
          onOpen={(tab) => {
            if (tab === 'advisor') {
              openChat();
            } else if (tab === 'tasks') {
              openTasksDrawer();
            } else {
              openAskHauser();
            }
          }}
          onClose={() => {
            closeAskHauser();
            closeChat();
          }}
          propertyId={propertyId}
        />
        </ToolsSurface>
      )}

      {/* Add Property Modal — for mobile on-page selector */}
      <AddPropertyModal open={addPropertyOpen} onClose={() => setAddPropertyOpen(false)} />
    </Box>
  );
};

export const DashboardLayout = () => {
  const { canAccessAdmin, isAdvisor } = useUserRole();

  // Pure admins/super-admins (no advisor role) use AdminDashboardLayout
  if (canAccessAdmin && !isAdvisor) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <DashboardLayoutProvider>
      <DashboardLayoutContent />
    </DashboardLayoutProvider>
  );
};
